/**
 * Inventory Items Hook
 * Custom hook for managing inventory items data and operations
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../../contexts/AuthContext';
import toast from 'react-hot-toast';

export const useInventoryItems = (options = {}) => {
  const { 
    autoFetch = true,
    productTypeId = null 
  } = options;
  
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch inventory items
  const fetchItems = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Ensure authorization header is set
      if (token && !axios.defaults.headers.common['Authorization']) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      // Using backend modular route structure
      const response = await axios.get('/api/inventory/items');
      setItems(response.data || []);
    } catch (err) {
      console.error('Error fetching inventory items:', err);
      setError(err.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Create new item
  const createItem = useCallback(async (itemData) => {
    try {
      console.log('🔥 FRONTEND CREATE ITEM - Sending data:', {
        itemData: JSON.stringify(itemData, null, 2),
        endpoint: '/api/inventory/items'
      });
      
      const response = await axios.post('/api/inventory/items', itemData);
      toast.success('Item created successfully');
      await fetchItems(); // Refresh list
      return response.data;
    } catch (err) {
      console.log('🔥 FRONTEND CREATE ITEM ERROR:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message
      });
      
      const errorMessage = err.response?.data?.error || 'Failed to create item';
      toast.error(errorMessage);
      throw err;
    }
  }, [fetchItems]);

  // Update existing item
  const updateItem = useCallback(async (itemId, itemData) => {
    try {
      const response = await axios.put(`/api/inventory/items/${itemId}`, itemData);
      toast.success('Item updated successfully');
      await fetchItems(); // Refresh list
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to update item';
      toast.error(errorMessage);
      throw err;
    }
  }, [fetchItems]);

  // Delete item
  const deleteItem = useCallback(async (itemId) => {
    try {
      await axios.delete(`/api/inventory/items/${itemId}`);
      toast.success('Item deleted successfully');
      await fetchItems(); // Refresh list
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to delete item';
      toast.error(errorMessage);
      throw err;
    }
  }, [fetchItems]);

  // Bulk delete items
  const bulkDeleteItems = useCallback(async (itemIds) => {
    try {
      await axios.delete('/api/inventory/items/bulk/delete', { 
        data: { itemIds } 
      });
      toast.success(`${itemIds.length} items deleted successfully`);
      await fetchItems(); // Refresh list
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to delete items';
      toast.error(errorMessage);
      throw err;
    }
  }, [fetchItems]);

  // Update item status
  const updateItemStatus = useCallback(async (itemId, status) => {
    try {
      console.log('🔥 STATUS UPDATE - Sending request:', {
        itemId,
        status,
        endpoint: `/api/inventory/items/${itemId}/status`,
        payload: { status }
      });

      const response = await axios.patch(`/api/inventory/items/${itemId}/status`, { status });
      
      console.log('🔥 STATUS UPDATE - Success response:', {
        status: response.status,
        data: response.data
      });

      toast.success('Status updated successfully');
      await fetchItems(); // Refresh list
      return response.data;
    } catch (err) {
      console.log('🔥 STATUS UPDATE - Error details:', {
        responseStatus: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message,
        itemId,
        newStatus: status
      });

      const errorMessage = err.response?.data?.error || 'Failed to update status';
      toast.error(errorMessage);
      throw err;
    }
  }, [fetchItems]);

  // Generate bulk item IDs
  const generateBulkIds = useCallback(async (productTypeId, quantity) => {
    try {
      const response = await axios.post('/api/inventory/items/generate-bulk', {
        productTypeId,
        quantity
      });
      toast.success(`Generated ${quantity} new items`);
      await fetchItems(); // Refresh list
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to generate items';
      toast.error(errorMessage);
      throw err;
    }
  }, [fetchItems]);

  // Filter items by various criteria
  const filterItems = useCallback((filters = {}) => {
    let filteredItems = [...items];
    
    if (filters.productTypeId && filters.productTypeId !== 'all') {
      filteredItems = filteredItems.filter(item => 
        item.productTypeId === filters.productTypeId
      );
    }
    
    if (filters.status && filters.status !== 'all') {
      filteredItems = filteredItems.filter(item => 
        item.status === filters.status
      );
    }
    
    if (filters.searchTerm) {
      const search = filters.searchTerm.toLowerCase();
      filteredItems = filteredItems.filter(item =>
        item.itemNumber?.toLowerCase().includes(search) ||
        item.serialNumber?.toLowerCase().includes(search) ||
        item.location?.toLowerCase().includes(search) ||
        item.notes?.toLowerCase().includes(search)
      );
    }
    
    return filteredItems;
  }, [items]);

  // Get items by status
  const getItemsByStatus = useCallback((status) => {
    return items.filter(item => item.status === status);
  }, [items]);

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (autoFetch && token) {
      fetchItems();
    }
  }, [autoFetch, token]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    items,
    loading,
    error,
    fetchItems,
    createItem,
    updateItem,
    deleteItem,
    bulkDeleteItems,
    updateItemStatus,
    generateBulkIds,
    filterItems,
    getItemsByStatus,
    // Computed values
    totalItems: items.length,
    availableCount: getItemsByStatus('AVAILABLE').length,
    inUseCount: getItemsByStatus('IN_USE').length,
    maintenanceCount: getItemsByStatus('MAINTENANCE').length,
    retiredCount: getItemsByStatus('RETIRED').length
  };
};